import React from "react";
import PropTypes from "prop-types";
import {
    Avatar,
    Box,
    IconButton,
    Skeleton,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    ArrowForwardRounded,
    SearchRounded,
    Close,
    VerifiedRounded,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory, useLocation } from "react-router-dom";
import EloBadge from "../common/EloBadge.jsx";
import { PageContainer } from "../layout/PageContainer";
import { useScrollLock } from "../common/lenisContext";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { colors } from "../../theme/colors";
import { toProfilePath } from "../../universal/v2Routes";
import { TOPBAR_XL_HEIGHT_PX } from "../layout/layoutConstants";

const MotionBox = motion(Box);
const RECENT_PLAYERS_KEY = "ouro_recent_player_searches_v1";
const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 200;
const RESULT_LIMIT = 8;
const SEARCH_EASE = [0.22, 1, 0.36, 1];

function readRecentPlayers() {
    if (typeof window === "undefined") return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(RECENT_PLAYERS_KEY) || "[]");
        return Array.isArray(parsed) ? parsed.slice(0, 5) : [];
    } catch {
        return [];
    }
}

function storeRecentPlayer(player) {
    if (typeof window === "undefined" || !player) return [];
    const key = String(player._id || player.steamId || "");
    if (!key) return readRecentPlayers();
    const compact = {
        _id: player._id,
        steamId: player.steamId,
        nickname: player.nickname,
        elo: player.elo,
        picture: player.picture,
        country: player.country,
        flagImageUrl: player.flagImageUrl,
        isOnline: player.isOnline,
        accountStatus: player.accountStatus,
        playerStatus: player.playerStatus,
    };
    const next = [
        compact,
        ...readRecentPlayers().filter(
            (item) => String(item?._id || item?.steamId || "") !== key
        ),
    ].slice(0, 5);
    try {
        window.localStorage.setItem(RECENT_PLAYERS_KEY, JSON.stringify(next));
    } catch {
        // Recent history is a progressive enhancement; search still works without it.
    }
    return next;
}

function getPresence(player) {
    if (!player?.isOnline) return { label: "Offline", color: "rgba(255,255,255,.28)" };
    const status = String(player?.playerStatus || "").toLowerCase();
    if (status.includes("matchmaking") || status.includes("search")) {
        return { label: "Searching", color: "#31D27C" };
    }
    if (status.includes("match")) return { label: "In Match", color: "#31D27C" };
    if (status.includes("lobby")) return { label: "In Lobby", color: "#31D27C" };
    return { label: "Online", color: "#31D27C" };
}

function PlayerResultRow({ player, selected, onSelect, onHover }) {
    const presence = getPresence(player);
    const verified = String(player?.accountStatus || "").toLowerCase() === "verified";

    return (
        <Box
            component="button"
            type="button"
            onClick={onSelect}
            onMouseEnter={onHover}
            sx={{
                appearance: "none",
                width: "100%",
                minHeight: { xs: "4.75rem", sm: "5.15rem" },
                px: { xs: "0.25rem", sm: "0.65rem" },
                py: "0.7rem",
                border: 0,
                borderBottom: "1px solid rgba(255,255,255,.055)",
                color: "inherit",
                background: selected
                    ? "linear-gradient(90deg, rgba(232,163,61,.09), rgba(232,163,61,.018) 58%, transparent)"
                    : "transparent",
                display: "grid",
                gridTemplateColumns: { xs: "auto minmax(0,1fr) auto", sm: "auto minmax(0,1fr) auto" },
                alignItems: "center",
                gap: { xs: "0.75rem", sm: "1.1rem" },
                textAlign: "left",
                cursor: "pointer",
                position: "relative",
                transition: "background 140ms ease, padding-left 140ms ease",
                "&::before": {
                    content: '\"\"',
                    position: "absolute",
                    left: 0,
                    top: "22%",
                    bottom: "22%",
                    width: "2px",
                    borderRadius: "2px",
                    background: colors.accent.main,
                    opacity: selected ? 0.82 : 0,
                    transition: "opacity 140ms ease",
                },
                "&:hover, &:focus-visible": {
                    outline: "none",
                    pl: { xs: "0.45rem", sm: "0.9rem" },
                    background: "linear-gradient(90deg, rgba(232,163,61,.075), rgba(232,163,61,.012) 60%, transparent)",
                    "&::before": { opacity: 0.72 },
                },
            }}
        >
            <Avatar
                src={player?.picture || undefined}
                alt=""
                sx={{
                    width: { xs: 44, sm: 50 },
                    height: { xs: 44, sm: 50 },
                    bgcolor: "rgba(255,255,255,.08)",
                    color: "rgba(255,255,255,.72)",
                    border: "1px solid rgba(255,255,255,.07)",
                    fontSize: "1rem",
                }}
            >
                {(player?.nickname || "?").slice(0, 1).toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "0.4rem", minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,.93)",
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.075rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {player?.nickname || "Unknown player"}
                    </Typography>
                    {verified ? (
                        <VerifiedRounded
                            aria-label="Verified player"
                            sx={{ color: colors.accent.main, fontSize: "1rem", flexShrink: 0 }}
                        />
                    ) : null}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.45rem", sm: "0.65rem" }, mt: "0.2rem", minWidth: 0 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0, mr: { xs: "0.1rem", sm: "0.2rem" } }}>
                        <EloBadge value={player?.elo || 0} size="nano" tooltip={false} />
                    </Box>
                    {player?.flagImageUrl ? (
                        <Box
                            component="img"
                            src={player.flagImageUrl}
                            alt=""
                            sx={{ width: 18, height: 12, objectFit: "cover", borderRadius: "1px", flexShrink: 0 }}
                        />
                    ) : null}
                    <Typography
                        component="span"
                        sx={{ color: "rgba(255,255,255,.46)", fontSize: "0.78rem", whiteSpace: "nowrap" }}
                    >
                        {player?.country || "Unknown"}
                    </Typography>
                    <Typography component="span" sx={{ color: "rgba(255,255,255,.18)", fontSize: "0.75rem" }}>
                        ·
                    </Typography>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: presence.color, flexShrink: 0 }} />
                    <Typography
                        component="span"
                        sx={{ color: "rgba(255,255,255,.5)", fontSize: "0.78rem", whiteSpace: "nowrap" }}
                    >
                        {presence.label}
                    </Typography>
            </Box>

            </Box>
            <ArrowForwardRounded
                sx={{
                    display: { xs: "none", sm: "block" },
                    color: selected ? "rgba(232,163,61,.76)" : "rgba(255,255,255,.19)",
                    fontSize: "1.05rem",
                    transition: "color 140ms ease, transform 140ms ease",
                    transform: selected ? "translateX(2px)" : "none",
                }}
            />
        </Box>
    );
}

PlayerResultRow.propTypes = {
    player: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onHover: PropTypes.func.isRequired,
};

function ResultSkeletons() {
    return [0, 1, 2].map((index) => (
        <Box
            key={index}
            sx={{ minHeight: { xs: "4.75rem", sm: "5.15rem" }, display: "flex", alignItems: "center", gap: "1rem", px: "0.4rem", borderBottom: "1px solid rgba(255,255,255,.045)" }}
        >
            <Skeleton variant="circular" width={48} height={48} sx={{ bgcolor: "rgba(255,255,255,.045)" }} />
            <Box sx={{ flex: 1 }}>
                <Skeleton width="24%" height={20} sx={{ bgcolor: "rgba(255,255,255,.045)" }} />
                <Skeleton width="18%" height={16} sx={{ bgcolor: "rgba(255,255,255,.03)" }} />
            </Box>
            <Skeleton width={70} height={28} sx={{ bgcolor: "rgba(255,255,255,.035)" }} />
        </Box>
    ));
}

export default function GlobalPlayerSearch({ open, onOpen, onClose, catalog, actions }) {
    const history = useHistory();
    const location = useLocation();
    const inputRef = React.useRef(null);
    const abortRef = React.useRef(null);
    const requestIdRef = React.useRef(0);
    const [query, setQuery] = React.useState("");
    const [focused, setFocused] = React.useState(false);
    const [debouncing, setDebouncing] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(-1);
    const [recentPlayers, setRecentPlayers] = React.useState(readRecentPlayers);
    const results = Array.isArray(catalog?.globalPlayerSearchResults) ? catalog.globalPlayerSearchResults : [];
    const searchState = catalog?.globalPlayerSearch || {};
    const trimmedQuery = query.trim();
    const hasSearchQuery = trimmedQuery.length >= MIN_QUERY_LENGTH;
    const displayedPlayers = hasSearchQuery ? results : recentPlayers;
    const isSearching = debouncing || searchState.loading;

    useScrollLock(open);
    useLockBodyScroll(open);

    const closeSearch = React.useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setQuery("");
        setSelectedIndex(-1);
        setFocused(false);
        setDebouncing(false);
        actions?.clearGlobalPlayerSearchResults?.();
        onClose();
    }, [actions, onClose]);

    React.useEffect(() => {
        const handleShortcut = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                if (!open) onOpen();
                return;
            }
            if (open && event.key === "Escape") {
                event.preventDefault();
                closeSearch();
            }
        };
        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    }, [closeSearch, onOpen, open]);

    React.useEffect(() => {
        if (!open) return undefined;
        const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 120);
        setRecentPlayers(readRecentPlayers());
        return () => window.clearTimeout(focusTimer);
    }, [open]);

    React.useEffect(() => {
        if (!open) return undefined;
        setSelectedIndex(-1);

        if (!hasSearchQuery) {
            setDebouncing(false);
            abortRef.current?.abort();
            abortRef.current = null;
            actions?.clearGlobalPlayerSearchResults?.();
            return undefined;
        }

        abortRef.current?.abort();
        abortRef.current = null;
        actions?.clearGlobalPlayerSearchResults?.();

        setDebouncing(true);

        const timer = window.setTimeout(() => {
            setDebouncing(false);
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            const requestId = `global-player-search-${++requestIdRef.current}`;
            actions?.searchUsers?.({
                q: trimmedQuery,
                limit: RESULT_LIMIT,
                offset: 0,
                includeTotal: false,
                quick: true,
                scope: "global",
                requestId,
                signal: controller.signal,
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timer);
    }, [actions, hasSearchQuery, open, trimmedQuery]);

    const openPlayer = React.useCallback(
        (player) => {
            const profileId = player?._id || player?.steamId;
            const profilePath = toProfilePath(profileId, location.pathname);
            if (!profilePath) return;
            setRecentPlayers(storeRecentPlayer(player));
            closeSearch();
            history.push(profilePath);
        },
        [closeSearch, history, location.pathname]
    );

    const handleInputKeyDown = (event) => {
        if (!displayedPlayers.length) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setSelectedIndex((current) => (current + 1) % displayedPlayers.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setSelectedIndex((current) =>
                current <= 0 ? displayedPlayers.length - 1 : current - 1
            );
        } else if (event.key === "Enter") {
            event.preventDefault();
            const player = displayedPlayers[selectedIndex >= 0 ? selectedIndex : 0];
            if (player) openPlayer(player);
        }
    };

    return (
        <AnimatePresence>
            {open ? (
                <>
                    <MotionBox
                        aria-hidden="true"
                        onClick={closeSearch}
                        initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                        animate={{ opacity: 1, backdropFilter: "blur(4px)" }}
                        exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                        transition={{ duration: 0.24, ease: "easeOut" }}
                        sx={{
                            position: "fixed",
                            inset: 0,
                            top: 0,
                            zIndex: 1590,
                            background: "rgba(3,4,6,.70)",
                        }}
                    />

                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1600,
                            pointerEvents: "none",
                        }}
                    >
                        <PageContainer sx={{ width: "100%", px: 0 }}>
                            <Box sx={{ display: "flex", gap: 0 }}>
                                <MotionBox
                                    role="dialog"
                                    aria-modal="true"
                                    aria-label="Search Ouro players"
                                    initial={{ opacity: 0, y: -12, filter: "blur(6px)" }}
                                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                    exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
                                    transition={{ duration: 0.38, ease: SEARCH_EASE }}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{
                                        pointerEvents: "auto",
                                        flex: 1,
                                        minWidth: 0,
                                        mt: 0,
                                        height: {
                                            xs: "calc(100vh - 4.25rem - env(safe-area-inset-bottom, 0px))",
                                            md: hasSearchQuery || recentPlayers.length
                                                ? `min(36rem, calc(100vh - ${TOPBAR_XL_HEIGHT_PX}px))`
                                                : "23rem",
                                        },
                                        borderRadius: 0,
                                        border: 0,
                                        borderBottom: "1px solid rgba(255,255,255,.075)",
                                        background: `
                                            radial-gradient(circle at 50% -12%, rgba(232,163,61,.095), transparent 34%),
                                            linear-gradient(180deg, rgba(16,17,20,.985), rgba(8,9,12,.99))
                                        `,
                                        boxShadow: "0 28px 80px rgba(0,0,0,.62)",
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box sx={{ px: { xs: "1rem", sm: "6vw", lg: "10vw" }, pt: { xs: "0.75rem", md: "1.25rem" }, flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                minHeight: { xs: "3.2rem", md: "3.35rem" },
                                                display: "grid",
                                                gridTemplateColumns: "auto minmax(0,1fr) auto",
                                                alignItems: "center",
                                                gap: { xs: "0.75rem", md: "1rem" },
                                                position: "relative",
                                                isolation: "isolate",
                                                borderBottom: `1px solid ${focused ? "rgba(255,255,255,.14)" : "rgba(255,255,255,.09)"}`,
                                                boxShadow: focused
                                                    ? "0 12px 26px -25px rgba(232,163,61,.42)"
                                                    : "none",
                                                transition: "border-color 180ms ease, box-shadow 180ms ease",
                                                "&::after": {
                                                    content: "\"\"",
                                                    position: "absolute",
                                                    left: 0,
                                                    right: 0,
                                                    bottom: "-1px",
                                                    height: "1px",
                                                    pointerEvents: "none",
                                                    background: `linear-gradient(90deg, transparent 0%, rgba(232,163,61,.16) 18%, rgba(244,194,105,.68) 50%, rgba(232,163,61,.16) 82%, transparent 100%)`,
                                                    opacity: focused ? 1 : 0,
                                                    transform: focused ? "scaleX(1)" : "scaleX(.72)",
                                                    transformOrigin: "center",
                                                    boxShadow: focused ? "0 0 8px rgba(232,163,61,.12)" : "none",
                                                    transition: "opacity 180ms ease, transform 220ms cubic-bezier(.22,1,.36,1)",
                                                },
                                            }}
                                        >
                                            <SearchRounded sx={{ color: focused ? "rgba(244,194,105,.86)" : "rgba(255,255,255,.38)", fontSize: { xs: "1.25rem", md: "1.4rem" }, transition: "color 180ms ease" }} />
                                            <Box
                                                ref={inputRef}
                                                component="input"
                                                value={query}
                                                onChange={(event) => setQuery(event.target.value)}
                                                onFocus={() => setFocused(true)}
                                                onBlur={() => setFocused(false)}
                                                onKeyDown={handleInputKeyDown}
                                                placeholder="Search by nickname or Steam ID..."
                                                aria-label="Search by nickname or Steam ID"
                                                autoComplete="off"
                                                spellCheck={false}
                                                maxLength={64}
                                                sx={{
                                                    width: "100%",
                                                    minWidth: 0,
                                                    border: 0,
                                                    outline: 0,
                                                    background: "transparent",
                                                    color: "rgba(255,255,255,.94)",
                                                    caretColor: "#E8A33D",
                                                    fontFamily: "inherit",
                                                    fontWeight: 500,
                                                    fontSize: { xs: "0.98rem", sm: "1.05rem", md: "1.1rem" },
                                                    letterSpacing: "-.015em",
                                                    "&::placeholder": { color: "rgba(255,255,255,.28)", opacity: 1 },
                                                }}
                                            />
                                            <IconButton
                                                onClick={closeSearch}
                                                aria-label="Close search"
                                                disableRipple
                                                sx={{
                                                    width: "2.25rem",
                                                    height: "2.25rem",
                                                    borderRadius: "0.55rem",
                                                    color: "rgba(255,255,255,.58)",
                                                    backgroundColor: "rgba(255,255,255,.04)",
                                                    transition: "background-color 140ms ease, color 140ms ease",
                                                    "&:hover": {
                                                        color: "rgba(255,255,255,.9)",
                                                        backgroundColor: "rgba(255,255,255,.08)",
                                                    },
                                                }}
                                            >
                                                <Close sx={{ fontSize: "1.25rem" }} />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box sx={{ px: { xs: "1rem", sm: "6vw", lg: "10vw" }, pt: "1.25rem", pb: "0.5rem", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <Typography sx={{ color: "rgba(255,255,255,.38)", fontSize: "0.67rem", fontWeight: 800, letterSpacing: ".17em" }}>
                                            {hasSearchQuery ? `RESULTS FOR \"${trimmedQuery.toUpperCase()}\"` : recentPlayers.length ? "RECENT PLAYERS" : "PLAYER SEARCH"}
                                        </Typography>
                                        {isSearching ? (
                                            <Typography sx={{ color: alpha(colors.accent.main, 0.72), fontSize: "0.67rem", letterSpacing: ".12em" }}>SEARCHING</Typography>
                                        ) : null}
                                    </Box>

                                    <Box sx={{ px: { xs: "1rem", sm: "6vw", lg: "10vw" }, flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", scrollbarColor: "rgba(255,255,255,.12) transparent" }}>
                                        {isSearching && hasSearchQuery ? <ResultSkeletons /> : null}
                                        {!isSearching && searchState.error && hasSearchQuery ? (
                                            <Box sx={{ py: "2rem", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
                                                <Typography sx={{ color: "rgba(255,255,255,.78)", fontWeight: 650 }}>Search is temporarily unavailable</Typography>
                                                <Typography sx={{ color: "rgba(255,255,255,.4)", mt: "0.3rem", fontSize: "0.88rem" }}>Please try again in a moment.</Typography>
                                            </Box>
                                        ) : null}
                                        {!isSearching && !searchState.error && displayedPlayers.map((player, index) => (
                                            <PlayerResultRow
                                                key={player?._id || player?.steamId || index}
                                                player={player}
                                                selected={selectedIndex === index}
                                                onHover={() => setSelectedIndex(index)}
                                                onSelect={() => openPlayer(player)}
                                            />
                                        ))}
                                        {!isSearching && !searchState.error && hasSearchQuery && results.length === 0 ? (
                                            <Box sx={{ py: { xs: "2.5rem", md: "3.5rem" }, textAlign: "center" }}>
                                                <Typography sx={{ color: "rgba(255,255,255,.82)", fontWeight: 700, fontSize: "1.05rem" }}>No players found</Typography>
                                                <Typography sx={{ color: "rgba(255,255,255,.38)", mt: "0.35rem", fontSize: "0.88rem" }}>Try another nickname or Steam ID.</Typography>
                                            </Box>
                                        ) : null}
                                        {!hasSearchQuery && recentPlayers.length === 0 ? (
                                            <Box sx={{ py: { xs: "3.25rem", md: "4.5rem" }, textAlign: "center" }}>
                                                <Typography sx={{ color: "rgba(255,255,255,.83)", fontWeight: 700, fontSize: { xs: "1.05rem", md: "1.15rem" } }}>Search the Ouro network</Typography>
                                                <Typography sx={{ color: "rgba(255,255,255,.36)", mt: "0.45rem", fontSize: "0.9rem" }}>Find players by nickname or Steam ID.</Typography>
                                            </Box>
                                        ) : null}
                                        {!hasSearchQuery && trimmedQuery.length > 0 ? (
                                            <Typography sx={{ color: "rgba(255,255,255,.3)", py: "1rem", fontSize: "0.8rem" }}>Enter at least {MIN_QUERY_LENGTH} characters.</Typography>
                                        ) : null}
                                    </Box>

                                </MotionBox>
                            </Box>
                        </PageContainer>
                    </Box>
                </>
            ) : null}
        </AnimatePresence>
    );
}

GlobalPlayerSearch.propTypes = {
    open: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    catalog: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
};
